import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import api from "../lib/axios";
import type { DashboardSummary, SaleRecord, TopProduct, ProfitMarginData, LowStockProduct } from "../types/sales";
import type { Store } from "../types/store";
import type { AppDispatch, RootState } from "../store";

export interface DateFilter {
  startDate: string | null;
  endDate: string | null;
  /** Comparison window start — computed from the preset or custom range duration */
  priorStartDate: string | null;
  /** Comparison window end */
  priorEndDate: string | null;
  /** null = all-time; 'custom' = user-entered dates; otherwise a preset key */
  preset: string | null;
}

interface DashboardState {
  profitMarginData: ProfitMarginData[];
  profitMarginDataLoading: boolean;
  profitMarginInitialized: boolean;
  topProducts: TopProduct[];
  topProductsLoading: boolean;
  topProductsInitialized: boolean;
  recentSales: SaleRecord[];
  recentSalesLoading: boolean;
  recentSalesInitialized: boolean;
  adminDashboardOverview: DashboardSummary | null;
  adminDashboardOverviewLoading: boolean;
  overviewInitialized: boolean;
  lowStockProducts: LowStockProduct[];
  lowStockProductsLoading: boolean;
  lowStockInitialized: boolean;
  selectedStore: Store | null;
  dateFilter: DateFilter;
  error: string | null;
}

const initialState: DashboardState = {
  profitMarginData: [],
  profitMarginDataLoading: false,
  profitMarginInitialized: false,
  topProducts: [],
  topProductsLoading: false,
  topProductsInitialized: false,
  recentSales: [],
  recentSalesLoading: false,
  recentSalesInitialized: false,
  adminDashboardOverview: null,
  adminDashboardOverviewLoading: false,
  overviewInitialized: false,
  lowStockProducts: [],
  lowStockProductsLoading: false,
  lowStockInitialized: false,
  selectedStore: null,
  dateFilter: { startDate: null, endDate: null, priorStartDate: null, priorEndDate: null, preset: null },
  error: null,
};

// -------------------- Helpers -------------------- //

function buildQueryParams(
  selectedStoreId: string | undefined,
  dateFilter: DateFilter
): string {
  const params = new URLSearchParams();
  if (selectedStoreId) params.set("storeId", selectedStoreId);
  if (dateFilter.startDate) params.set("startDate", dateFilter.startDate);
  if (dateFilter.endDate) params.set("endDate", dateFilter.endDate);
  if (dateFilter.priorStartDate) params.set("priorStartDate", dateFilter.priorStartDate);
  if (dateFilter.priorEndDate) params.set("priorEndDate", dateFilter.priorEndDate);
  const str = params.toString();
  return str ? `?${str}` : "";
}

// -------------------- Thunks -------------------- //

export const fetchProfitMarginData = createAsyncThunk<
  ProfitMarginData[],
  void,
  { state: RootState, rejectValue: string }
>("dashboard/fetchProfitMarginData", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedStoreId = state.dashboard.selectedStore?.id;
    const dateFilter = state.dashboard.dateFilter;

    const queryParams = buildQueryParams(selectedStoreId, dateFilter);

    const { data } = await api.get(`/dashboard/profit-margin${queryParams}`);
    return data.data as ProfitMarginData[];
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const fetchRecentSales = createAsyncThunk<
  SaleRecord[],
  void,
  { state: RootState, rejectValue: string }
>("dashboard/fetchRecentSales", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedStoreId = state.dashboard.selectedStore?.id;
    const dateFilter = state.dashboard.dateFilter;

    const queryParams = buildQueryParams(selectedStoreId, dateFilter);

    const { data } = await api.get(`/dashboard/recent-sales${queryParams}`);
    return data.data as SaleRecord[];
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const fetchTopProducts = createAsyncThunk<
  TopProduct[],
  void,
  { state: RootState, rejectValue: string }
>("dashboard/fetchTopProducts", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedStoreId = state.dashboard.selectedStore?.id;
    const dateFilter = state.dashboard.dateFilter;

    const queryParams = buildQueryParams(selectedStoreId, dateFilter);

    const { data } = await api.get(`/dashboard/top-products${queryParams}`);
    return data.data as TopProduct[];
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const fetchAdminDashboardOverview = createAsyncThunk<
  DashboardSummary,
  void,
  { state: RootState, rejectValue: string }
>("dashboard/fetchAdminDashboardOverview", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedStoreId = state.dashboard.selectedStore?.id;
    const dateFilter = state.dashboard.dateFilter;

    console.log("Selected store: ", state.dashboard.selectedStore);

    const queryParams = buildQueryParams(selectedStoreId, dateFilter);

    const { data } = await api.get(`/dashboard/admin-overview${queryParams}`);
    return data.data as DashboardSummary;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const fetchLowStockProducts = createAsyncThunk<
  LowStockProduct[],
  void,
  { state: RootState, rejectValue: string }
>("dashboard/fetchLowStockProducts", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedStoreId = state.dashboard.selectedStore?.id;

    const queryParams = selectedStoreId ? `?storeId=${selectedStoreId}` : '';

    const { data } = await api.get(`/dashboard/low-stock${queryParams}`);
    return data.data as LowStockProduct[];
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const refetchDashboard = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("dashboard/refetchDashboard", async (_, { dispatch }) => {
  await Promise.all([
    dispatch(fetchAdminDashboardOverview()),
    dispatch(fetchProfitMarginData()),
    dispatch(fetchRecentSales()),
    dispatch(fetchTopProducts()),
    dispatch(fetchLowStockProducts()),
  ]);
});


// -------------------- Slice -------------------- //

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    resetDashboardError(state) {
      state.error = null;
    },
    updateSelectedStore: (state, action: PayloadAction<Store | null>) => {
      state.selectedStore = action.payload;
    },
    updateDateFilter: (state, action: PayloadAction<DateFilter>) => {
      state.dateFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Profit Margin
    builder.addCase(fetchProfitMarginData.pending, (state) => {
      state.profitMarginDataLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProfitMarginData.fulfilled, (state, action: PayloadAction<ProfitMarginData[]>) => {
      state.profitMarginData = action.payload;
      state.profitMarginDataLoading = false;
      state.profitMarginInitialized = true;
    });
    builder.addCase(fetchProfitMarginData.rejected, (state, action) => {
      state.profitMarginDataLoading = false;
      state.profitMarginInitialized = true;
      state.error = action.payload || "Failed to fetch profit margin data";
    });

    // Recent Sales
    builder.addCase(fetchRecentSales.pending, (state) => {
      state.recentSalesLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRecentSales.fulfilled, (state, action: PayloadAction<SaleRecord[]>) => {
      state.recentSales = action.payload;
      state.recentSalesLoading = false;
      state.recentSalesInitialized = true;
    });
    builder.addCase(fetchRecentSales.rejected, (state, action) => {
      state.recentSalesLoading = false;
      state.recentSalesInitialized = true;
      state.error = action.payload || "Failed to fetch recent sales";
    });

    // Top Products
    builder.addCase(fetchTopProducts.pending, (state) => {
      state.topProductsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTopProducts.fulfilled, (state, action: PayloadAction<TopProduct[]>) => {
      state.topProducts = action.payload;
      state.topProductsLoading = false;
      state.topProductsInitialized = true;
    });
    builder.addCase(fetchTopProducts.rejected, (state, action) => {
      state.topProductsLoading = false;
      state.topProductsInitialized = true;
      state.error = action.payload || "Failed to fetch top products";
    });

    // Admin Dashboard Overview
    builder.addCase(fetchAdminDashboardOverview.pending, (state) => {
      state.adminDashboardOverviewLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminDashboardOverview.fulfilled, (state, action: PayloadAction<DashboardSummary>) => {
      state.adminDashboardOverview = action.payload;
      state.adminDashboardOverviewLoading = false;
      state.overviewInitialized = true;
    });
    builder.addCase(fetchAdminDashboardOverview.rejected, (state, action) => {
      state.adminDashboardOverviewLoading = false;
      state.overviewInitialized = true;
      state.error = action.payload || "Failed to fetch dashboard overview";
    });

    // Low Stock Products
    builder.addCase(fetchLowStockProducts.pending, (state) => {
      state.lowStockProductsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchLowStockProducts.fulfilled, (state, action: PayloadAction<LowStockProduct[]>) => {
      state.lowStockProducts = action.payload;
      state.lowStockProductsLoading = false;
      state.lowStockInitialized = true;
    });
    builder.addCase(fetchLowStockProducts.rejected, (state, action) => {
      state.lowStockProductsLoading = false;
      state.lowStockInitialized = true;
      state.error = action.payload || "Failed to fetch low stock products";
    });
  },
});

export const { resetDashboardError, updateSelectedStore, updateDateFilter } = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;
